import React from 'react';
import { useNavigate } from "react-router-dom";
import Markdown from 'marked-react';
import { RoundedDiv } from "../DashboardGraph/__styled__/DashboardGraph";
import { dateParse, cleanLink } from './utils';

// Custom marked-react renderer overrides.
// marked-react calls these with `this` = the ReactRenderer instance, so we can
// delegate back to the internal helpers (e.g. this.codespan) as needed.
// - code()     → fenced ``` blocks  → display:block  → notes-code-block class
// - codespan() → inline ` backtick` → display:inline → notes-code-inline class
const guidanceRenderer = {
  code(text, lang) {
    // Return a block <code> with our shared class — no <pre> wrapper needed
    // since .guidance code.notes-code-block already sets display:block.
    return React.createElement('code', { className: 'guidance-code-block', key: `code-${lang}-${text.slice(0, 8)}` }, text);
  },
  codespan(text) {
    return React.createElement('code', { className: 'guidance-code-inline', key: `codespan-${text.slice(0, 8)}` }, text);
  },
};

export default function ReportTemplate(props)  {
  const { headerData, params, isMyOwnTest, testid, showTable, testedURLsLinkList, newData, icon, showBackButton } = props;
  const navigate = useNavigate();

  const navToQuestion = (event, questionNum, url, notes, score, capability) => {
    event.preventDefault();
    navigate("/ScoringScreen", {
      state: { testId: testid, question: questionNum, url, notes, score, capability, isEditCompleted: true }
    });
  }

  return (
    <div id="rcontainer">
        {headerData && (
            <div id="topReportInfo">
              <h1 className="page-header">{`${headerData.company} Accessibility Report`}</h1>
              <p id="scoreh2">{`Score: ${(params.score || headerData.score).toFixed(0)}%`}</p>
              <p id="dateLbl">{`Test Completion Date: ${dateParse(headerData.date).replaceAll(".", "")}`}</p>
              <p>{`Domains Tested: ${headerData.urlTested}`}</p>
            </div>
        )}
        <figcaption id="fig-caption">
          <h2 id="scoreh2v2">Response Feedback</h2>
        </figcaption>
        {isMyOwnTest && (<div className="bottom-margin" id="modify-test">
          <a
            href={`/ScoringScreen?testId=${testid}`}
            className="link-to-response"
          >
            Modify this test
          </a>
        </div>)}
        {showTable &&
          <div className="link-list">
            <h2>List of Links Tested</h2>
            <p>This list contains the pages that were tested and used in the report below.</p>
            <ul>
              {testedURLsLinkList.map((link, x) => (
                <li key={`link-item-${x}`}>{link.title}: <a href={link.url}>{link.url}</a></li>
              ))}
            </ul>
          </div>}
        {showTable &&
          <RoundedDiv className="print-border">
            <div id="table">
              <div className="Section1">
                <table id="rtable" aria-labelledby="fig-caption">
                  <thead id="reportHead">
                    <tr>
                      <th scope="col" id="accessibilityCol">
                        Accessibility Indicator
                      </th>
                      <th scope="col" id="guidanceCol">
                        Guidance
                      </th>
                      <th scope="col" id="urlCol">
                        URL Tested &#38; Notes
                      </th>
                      <th scope="col" id="capabilityCol" className="capability">
                        Capability
                      </th>
                      {!showBackButton && isMyOwnTest &&
                        <th scope="col" id="actionsCol" className="edit">
                          Actions
                        </th>
                      }
                    </tr>
                  </thead>
                  <tbody id="reportBody">
                    {newData.length > 0 && newData.map((data, idx) => (
                      <tr key={idx} data-criteria-id={data.criteriaid}>
                        <td scope="row" className="reportCells accessibility"><p><b>{data.criteria}</b></p></td>
                        <td className={`reportCells guidance`}><Markdown renderer={guidanceRenderer}>{data.guidance}</Markdown></td>
                        <td className={`reportCells urlNotes`}>
                          {/* Outer div instead of p: notesWebHtml can contain block-level
                              <code> elements which are invalid inside <p>, causing the
                              browser to close the <p> early and break rendering. */}
                          <div>
                          <ul className="links-list">
                          {data.url.includes("http") && data.url.split("http").map((link, x) => {
                            if (link.length === 0) return;

                            if (!link.includes("/")) {
                              return link !== "" &&
                                <div key={`link-list-${x}`}>
                                  <p>{link.replaceAll("\\n", "\n")}</p>
                                </div>
                            }

                            const linkString = cleanLink(link);

                            const pageLink = testedURLsLinkList.filter(testedUrl => testedUrl.url === linkString);
                            
                            return pageLink.length &&
                              <li key={`link-list-${x}`}>
                                <a className="links-markdown" href={pageLink[0].url} target="_blank">{pageLink[0].title}</a>
                                <img src={icon} alt="(opens link in new tab)" className="open-new-tab" />
                              </li>
                          })}
                          </ul>

                          {!data.url.includes("http") &&
                            <p>{data.url.replaceAll("\\n", "\n")}</p>
                          }
                          {/*
                            notesWebHtml is pre-processed by buildNotesWebHtml() in index.js:
                            HTML special characters are entity-encoded (so user-typed tags like
                            <body> display as literal text), tag spans are wrapped in <code>,
                            backtick spans are wrapped in <code>, newlines become <br />.
                            The marginTop restores the spacing that a <p> wrapper previously
                            provided before this was changed to a <div> to allow block-level
                            <code> children (invalid inside <p>).
                          */}
                          <div style={{ marginTop: '1em' }} dangerouslySetInnerHTML={{ __html: data.notesWebHtml ?? data.notes.replaceAll("\\n", "\n") }} />
                          </div>
                        </td>
                        <td className="reportCells" id="capabilityCol">{data.capability}</td>
                        {!showBackButton && isMyOwnTest &&
                          <td className="editButton">
                            <a href="#" className="link-to-response" onClick={(event) => navToQuestion(event, data.criteriaid, data.url, data.urlNotes, data.score, data.capability)}> Edit Response</a>
                          </td>
                        }
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </RoundedDiv>
        }
        {!showTable &&
          <h2>There is no feedback to display for this account.</h2>
        }
    </div>
  )
}